import { Control, Controller, SubmitErrorHandler, SubmitHandler } from 'react-hook-form';
import Modal from '@components/common/modal';
import Button from '@components/common/button';
import { useToast } from '@components/common/toast';
import Icon from '@components/common/assets';
import { JDModalType } from '@components/position/jd/apply/modals';
import { useAdditionalItems } from '@repository/match';
import Text from '@components/position/jd/apply/modals/additionalInputModal/Text';
import SingleChoice from '@components/position/jd/apply/modals/additionalInputModal/SingleChoice';
import MultiChoice from '@components/position/jd/apply/modals/additionalInputModal/MultiChoice';
import AttachFile from '@components/position/jd/apply/modals/additionalInputModal/AttachFile';
import {
  FormDataType,
  useAdditionInfoHookFormContext,
  useAdditionalInputContext,
} from '@components/position/jd/apply/modals/additionalInputModal/AdditionalInputContext';
import Dropdown from '@components/position/jd/apply/modals/additionalInputModal/Dropdown';
import LeaveModal from '@components/position/jd/apply/modals/LeaveModal';
import { AdditionInfoVO } from '@domain/vo/match/ResumeAdditionInfoVO';
import { AdditionalItemType } from '@domain/constant/profileRequirementType';
import useModal from '@utils/hooks/useModal';

import { useKeyDown } from '@utils/hooks/useKeyboard';
import classnames from 'classnames/bind';
import styles from './additionalInputModal.module.scss';
const cx = classnames.bind(styles);

interface AdditionalInputItemProps extends AdditionInfoVO {
  control: Control;
}

const AdditionalInputItem = (props: AdditionalInputItemProps) => {
  switch (props.formType) {
    case 'TEXT':
      return <Text {...props} />;
    case 'SINGLE_CHOICE':
      return <SingleChoice {...props} />;
    case 'MULTI_CHOICE':
      return <MultiChoice {...props} />;
    case 'DROPDOWN':
      return <Dropdown {...props} />;
    case 'ADDITION_ATTACH_FILE':
      return <AttachFile {...props} />;
  }
};

interface Props {
  visible: boolean;
  setModalState: (modalState: JDModalType) => void;
  positionSn: number;
}

const AdditionalInputModal = ({ visible, setModalState, positionSn }: Props) => {
  const Toast = useToast();
  const {
    control,
    handleSubmit,
    formState: { isDirty },
    getValues,
    reset,
  } = useAdditionInfoHookFormContext();
  const { data: additionalItem } = useAdditionalItems(positionSn);
  const { tempFormData, saveTempData, saveFormData, setIsCompleteFalse, savePreviewData } = useAdditionalInputContext();
  const { closeModal: closeLeaveModal, isModalOpen, openModal: openLeaveModal } = useModal();

  const handleClickPrevButton = () => {
    if (isDirty) {
      openLeaveModal();
    } else {
      setModalState('EASY_APPLY');
      reset(tempFormData, { keepValues: true });
    }
  };

  const handleClickLeaveButton = () => {
    closeLeaveModal();
    setModalState('EASY_APPLY');
    reset(tempFormData);
  };

  const onSubmit: SubmitHandler<FormDataType> = (data) => {
    if (!additionalItem) return;

    Toast({ content: '성공적으로 저장되었습니다.', type: 'success', iconType: 'check' });
    saveTempData(data);
    saveFormData(data, additionalItem.additionInfo);
    savePreviewData(data, additionalItem.additionInfo);
    setModalState('EASY_APPLY');
  };

  const onInvalid: SubmitErrorHandler<FormDataType> = (_error) => {
    if (!additionalItem) return;

    const data = getValues();
    setIsCompleteFalse();
    saveTempData(data);
    savePreviewData(data, additionalItem.additionInfo);
    reset(data, { keepErrors: true });
    Toast({ content: '저장에 필요한 정보를 작성해주세요.', type: 'error', iconType: 'info' });
  };

  useKeyDown(() => {
    visible && handleClickPrevButton();
  }, 'Escape');

  return (
    <Modal visible={visible} onClickBg={handleClickPrevButton}>
      <section className={cx('layer')}>
        <a className={cx('closeBtn')} onClick={handleClickPrevButton} role="button">
          <Icon name="closeLight" width={32} height={32} />
        </a>

        <h2 className={cx('title')}>
          <Icon name="fileAddLight" width={40} height={40} />
          추가 입력
        </h2>

        <form className={cx('formArea')} onSubmit={handleSubmit(onSubmit, onInvalid)}>
          {additionalItem?.additionInfo.map((item) => (
            <Controller
              key={item.additionInfoSn}
              name={`${item.additionInfoSn}` as never}
              control={control}
              rules={{ required: item.required ? getErrorMessage(item.formType) : false }}
              render={({ field }) => (
                <AdditionalInputItem
                  {...item}
                  key={item.additionInfoSn}
                  required={item.required}
                  {...field}
                  control={control}
                />
              )}
            />
          ))}

          <div className={cx('btnArea')}>
            <Button className={cx('btn')} onClick={handleClickPrevButton} theme="secondary" type="button">
              이전
            </Button>
            <Button className={cx('btn')} type="submit">
              저장
            </Button>
          </div>
        </form>
      </section>

      {/* 입력 후 나가시겠어요? 모달 */}
      <LeaveModal visible={isModalOpen} onClose={closeLeaveModal} onOk={handleClickLeaveButton} />
    </Modal>
  );
};

export default AdditionalInputModal;

const getErrorMessage = (formType: AdditionalItemType) => {
  switch (formType) {
    case 'TEXT':
      return '내용을 입력해주세요.';
    case 'SINGLE_CHOICE':
    case 'MULTI_CHOICE':
    case 'DROPDOWN':
      return '내용을 선택해주세요.';
    case 'ADDITION_ATTACH_FILE':
      return '첨부 파일을 추가해주세요.';
    default:
      return '';
  }
};
